import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText,
  Calendar,
  BarChart2,
  Wrench,
  Cog,
  Users,
  Shield,
  Lock,
  PackagePlus,
  PackageSearch,
  FolderCog,
  Code,
  ClipboardClock,
  History,
  MessageSquare,
  Trophy,
  Keyboard,
  Radio
} from 'lucide-react';
import { getTenantLogo } from '../utils/getTenantLogo';
import { Reports } from './Reports';
import { ImpersonationPanel } from './Admin/ImpersonationPanel';
import { SupplyRequestForm } from './SupplyRequestForm';
import { OrderTracking } from './OrderTracking';
import { Settings } from './Settings';
import { DebuggerModal } from './DebuggerModal';
import { GerArquivosPageRefatorada } from './pages/GerArquivosPageRefatorada';
import { usePermissions } from '../contexts/PermissionContext';
import { useUnifiedFeaturePermissions, useUnifiedFeaturePermission } from '../hooks/useUnifiedPermissions';
import { openToolsDocker } from '../lib/corel/tools';
import { terminalLog } from '../lib/terminalLogger';
import { IS_PRODUCTION } from '../config';
import { corelCommunication } from '../lib/corel/communication';
import { SaveTempModal } from './SaveTempModal/SaveTempModal';
import OrderDetailsTempWindow from './OrderDetailsTemp/OrderDetailsTempWindow';
import { SaveTempDraft } from '../types/saveTemp';
import { Button, LoadingSpinner, PermissionDenied } from './ui';
import { CampanhasBadge } from './Quiz/CampanhasBadge';
import { RoleSelector } from './MainMenu/RoleSelector';

interface MainMenuProps {
  user: {
    email: string;
    nome: string;
    tipo: string;
  };
  onLogout: () => void;
  onNavigate: (page: 'login' | 'main' | 'pedidos' | 'historico' | 'admin' | 'dashboard' | 'messages' | 'campanhas' | 'shortcuts') => void;
  onOpenModal: (title: string, content: React.ReactNode, size?: 'sm' | 'md' | 'lg') => void;
  onNewOrder: () => void;
}

// Catálogo local removido: a grade/modal de ferramentas passa a existir no Docker 2 (C#)

export function MainMenu({ user, onLogout, onNavigate, onOpenModal, onNewOrder }: MainMenuProps) {
  const [showReports, setShowReports] = useState(false);
  const [showOrderTracking, setShowOrderTracking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);
  const [showSaveTemp, setShowSaveTemp] = useState(false);
  const [tempDraft, setTempDraft] = useState<SaveTempDraft | null>(null);
  const [currentPage, setCurrentPage] = useState<'menu' | 'ger-arquivos'>('menu');
  const [toolsLoading, setToolsLoading] = useState(false);
  const navigate = useNavigate();
  const { role, permissions, userInfo } = usePermissions();
  
  // 🛡️ Permissões de funcionalidades do menu (admin sempre tem acesso)
  const featurePermissions = useUnifiedFeaturePermissions([
    'general.can_manage_files',                  // Ger. Arquivos
    'general.can_access_settings',               // Configurações
    'general.can_view_history',                  // Histórico
    'orders.can_access',                         // Pedidos
    'orders.can_track',                          // Acompanhar Pedidos
    'orders.can_access_campaigns',               // Campanhas Disponíveis
    'orders.can_request_supplies',               // Solicitar Insumos (Produção)
    'general.can_access_client_section',         // Seção menu cliente/revenda/vendedor
    'general.can_access_production_section',     // Seção menu produção
    'general.can_create_new_order',              // Botão "Novo Orçamento"
    'admin.can_access',                          // Seção menu admin
  ]);

  // 🔍 DIAGNÓSTICO: Log das permissões do MainMenu
  // ✅ CORREÇÃO: Todos os hooks devem ser chamados antes de qualquer return condicional
  const canAccessTools = useUnifiedFeaturePermission('general.can_access_tools');
  const canSave = useUnifiedFeaturePermission('general.can_save');
  const _canExport = useUnifiedFeaturePermission('general.can_export'); // hook must stay for consistent hook order
  const canViewReports = useUnifiedFeaturePermission('general.can_view_reports');
  const canAccessCalendar = useUnifiedFeaturePermission('general.can_access_calendar');
  const canAccessDebugger = useUnifiedFeaturePermission('admin.can_access_debugger');
  
  // Log de diagnóstico removido — issue resolvida (2026-03-26)

  // ✅ CORREÇÃO: Validação de role movida para renderização condicional no JSX
  // NÃO usar early return após hooks - isso causa erro "Rendered fewer hooks than expected"

  // Função helper para fechar todos os modais/overlays
  const closeAllModals = () => {
    setShowReports(false);
    setShowOrderTracking(false);
    setShowSettings(false);
    setShowSaveTemp(false);
  };

  const handleOpenTools = async () => {
    try {
      if (toolsLoading) return;
      setToolsLoading(true);
      terminalLog.info('UI: menutools:start');
      
      // Adicionar timeout de 10 segundos
      let timeoutId: NodeJS.Timeout | null = null;
      const timeoutPromise = new Promise<{ ok: false; error: string }>((resolve) => {
        timeoutId = setTimeout(() => {
          resolve({
            ok: false,
            error: 'Tempo limite excedido ao abrir Ferramentas. Verifique se o Docker 2 está rodando.'
          });
        }, 10000);
      });

      const result = await Promise.race([
        openToolsDocker().finally(() => {
          // Limpar timeout se a operação completar antes do timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }),
        timeoutPromise
      ]);

      if (!result.ok) {
        terminalLog.warn('UI: menutools:error', { error: result.error });
        const msg = result.error || 'Não foi possível abrir Ferramentas. Tente novamente.';
        const title = msg.toLowerCase().includes('montatec') ? 'Montatec' : 'Ferramentas';
        onOpenModal(title, (
          <div className="p-3">
            <p className="text-sm text-gray-300 mb-3">{msg}</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => onOpenModal('', null)}>Fechar</Button>
              <Button variant="warning" size="sm" onClick={async () => { onOpenModal('', null); await handleOpenTools(); }}>Tentar novamente</Button>
            </div>
          </div>
        ), 'sm');
      } else {
        terminalLog.info('UI: menutools:success');
      }
    } finally {
      setToolsLoading(false);
    }
  };

  const renderClientRevendaButtons = () => {
    if (userInfo?.isAdmin) return null; // Admin tem seção própria
    if (!featurePermissions['general.can_access_client_section']?.allowed) return null;

    return (
      <>
        {/* Seção Principal */}
        <div className="space-y-1">
          {!IS_PRODUCTION && (
            <Button
              variant="ghost"
              onClick={() => setShowSaveTemp(true)}
              icon={ClipboardClock}
              className="w-full justify-start"
            >
              Salvar Arquivos
            </Button>
          )}

          {featurePermissions['orders.can_access']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => navigate('/pedidos')}
              icon={FileText}
              className="w-full justify-start"
            >
              Pedidos
            </Button>
          )}

          {featurePermissions['general.can_manage_files']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => setCurrentPage('ger-arquivos')}
              icon={ClipboardClock}
              className="w-full justify-start"
            >
              Ger. Arquivos
            </Button>
          )}

          {featurePermissions['general.can_view_history']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => navigate('/historico')}
              icon={History}
              className="w-full justify-start"
            >
              Histórico
            </Button>
          )}
        </div>

        {/* Separador */}
        <div className="my-4 border-t border-navy-700"></div>

        {/* Seção Ferramentas e Acompanhamento */}
        <div className="space-y-1">
          {canAccessTools && (
            <Button
              variant="ghost"
              onClick={handleOpenTools}
              disabled={toolsLoading}
              loading={toolsLoading}
              icon={Wrench}
              className="w-full justify-start"
            >
              {toolsLoading ? 'Abrindo…' : 'Ferramentas'}
            </Button>
          )}

          {featurePermissions['orders.can_track']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => {
                closeAllModals();
                setShowOrderTracking(true);
              }}
              icon={PackageSearch}
              className="w-full justify-start"
            >
              Acompanhar Pedidos
            </Button>
          )}

          {featurePermissions['orders.can_access_campaigns']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => {
                console.log('[MainMenu] Navegando para campanhas');
                closeAllModals();
                navigate('/campanhas');
              }}
              icon={Trophy}
              className="w-full justify-start"
            >
              Campanhas Disponíveis
              <CampanhasBadge />
            </Button>
          )}
        </div>

        {/* Separador + Seção Relatórios e Configurações — visível se ao menos um item estiver habilitado */}
        {(canViewReports || canAccessCalendar || featurePermissions['general.can_access_settings']?.allowed) && (
          <>
            <div className="my-4 border-t border-navy-700"></div>

            <div className="space-y-1">
              {canViewReports && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    closeAllModals();
                    setShowReports(true);
                  }}
                  icon={BarChart2}
                  className="w-full justify-start"
                >
                  Relatórios
                </Button>
              )}

              {canAccessCalendar && (
                <Button
                  variant="ghost"
                  onClick={() => onOpenModal('Agenda',
                    <div className="w-full h-96">
                      <Calendar className="w-full h-full text-gray-400" />
                      <p className="text-center mt-4">Funcionalidade em desenvolvimento</p>
                    </div>,
                    'lg'
                  )}
                  icon={Calendar}
                  className="w-full justify-start"
                >
                  Agenda
                </Button>
              )}

              {featurePermissions['general.can_access_settings']?.allowed && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    closeAllModals();
                    setShowSettings(true);
                  }}
                  icon={FolderCog}
                  className="w-full justify-start"
                >
                  Configurações
                </Button>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  const renderAdminButtons = () => {
    if (!featurePermissions['admin.can_access']?.allowed) return null;

    return (
      <>
        {/* Seção Principal */}
        <div className="space-y-1">
          {featurePermissions['orders.can_access']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => navigate('/pedidos')}
              icon={FileText}
              className="w-full justify-start"
            >
              Pedidos
            </Button>
          )}
        </div>

        {/* Separador */}
        <div className="my-4 border-t border-navy-700"></div>

        {/* Seção Administração */}
        <div className="space-y-1">
          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/admin');
            }}
            icon={Shield}
            className="w-full justify-start"
          >
            Painel Administrativo
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/admin/permissions');
            }}
            icon={Lock}
            className="w-full justify-start"
          >
            Permissões
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/dashboard');
            }}
            icon={BarChart2}
            className="w-full justify-start"
          >
            Dashboard Analítico
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/messages');
            }}
            icon={MessageSquare}
            className="w-full justify-start"
          >
            Gerenciar Mensagens
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/campanhas-admin');
            }}
            icon={Trophy}
            className="w-full justify-start"
          >
            Campanhas & Quiz
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              closeAllModals();
              navigate('/shortcuts');
            }}
            icon={Keyboard}
            className="w-full justify-start"
          >
            Gerenciar Atalhos
          </Button>

          <Button
            variant="ghost"
            onClick={() => onOpenModal('Simular Fluxo', <ImpersonationPanel />, 'md')}
            icon={Users}
            className="w-full justify-start"
          >
            Simular Fluxo
          </Button>
        </div>

        {/* Separador */}
        <div className="my-4 border-t border-navy-700"></div>

        {/* Seção Desenvolvimento */}
        <div className="space-y-1">
          <Button
            variant="ghost"
            onClick={() => setShowDebugger(true)}
            icon={Code}
            className="w-full justify-start"
          >
            Debugger CorelDraw
          </Button>

          <Button
            variant="ghost"
            onClick={async () => {
              try {
                terminalLog.info('UI: debug-communication:start');
                corelCommunication.sendActionGuarded(
                  'debug:load-html',
                  {
                    htmlFile: 'communication-debug'
                  },
                  (response) => {
                    if (response.status === 'ok') {
                      terminalLog.info('UI: debug-communication:success', {
                        htmlPath: response.result?.htmlPath,
                        dockerGuid: response.result?.dockerGuid
                      });
                    } else {
                      terminalLog.warn('UI: debug-communication:error', {
                        message: response.message || response.error
                      });
                    }
                  }
                );
              } catch (error) {
                terminalLog.error('UI: debug-communication:exception', {
                  error: error instanceof Error ? error.message : String(error)
                });
              }
            }}
            icon={Radio}
            className="w-full justify-start"
          >
            Debug de Comunicações
          </Button>

        </div>

        {/* Separador */}
        <div className="my-4 border-t border-navy-700"></div>

        {/* Seção Ferramentas e Relatórios */}
        <div className="space-y-1">
          {canAccessTools && (
            <Button
              variant="ghost"
              onClick={handleOpenTools}
              disabled={toolsLoading}
              loading={toolsLoading}
              icon={Wrench}
              className="w-full justify-start"
            >
              {toolsLoading ? 'Abrindo…' : 'Ferramentas'}
            </Button>
          )}

          {canAccessCalendar && (
            <Button
              variant="ghost"
              onClick={() => onOpenModal('Agenda', 
                <div className="w-full h-96">
                  <Calendar className="w-full h-full text-gray-400" />
                  <p className="text-center mt-4">Funcionalidade em desenvolvimento</p>
                </div>, 
                'lg'
              )}
              icon={Calendar}
              className="w-full justify-start"
            >
              Agenda
            </Button>
          )}

          {canViewReports && (
            <Button
              variant="ghost"
              onClick={() => {
                closeAllModals();
                setShowReports(true);
              }}
              icon={BarChart2}
              className="w-full justify-start"
            >
              Relatórios
            </Button>
          )}

          {featurePermissions['general.can_access_settings']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => {
                closeAllModals();
                setShowSettings(true);
              }}
              icon={FolderCog}
              className="w-full justify-start"
            >
              Configurações
            </Button>
          )}
        </div>
      </>
    );
  };

  const renderProducaoButtons = () => {
    if (userInfo?.isAdmin) return null; // Admin tem seção própria
    if (!featurePermissions['general.can_access_production_section']?.allowed) return null;

    return (
      <>
        {/* Seção Principal */}
        <div className="space-y-1">
          {featurePermissions['orders.can_access']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => navigate('/pedidos')}
              icon={FileText}
              className="w-full justify-start"
            >
              Pedidos
            </Button>
          )}

          {featurePermissions['orders.can_request_supplies']?.allowed && (
            <Button
              variant="ghost"
              onClick={() => onOpenModal('Solicitar Insumos', <SupplyRequestForm />, 'md')}
              icon={PackagePlus}
              className="w-full justify-start"
            >
              Solicitar Insumos
            </Button>
          )}
        </div>

        {/* Separador */}
        <div className="my-4 border-t border-navy-700"></div>

        {/* Seção Ferramentas */}
        <div className="space-y-1">
          {canAccessTools && (
            <Button
              variant="ghost"
              onClick={handleOpenTools}
              disabled={toolsLoading}
              loading={toolsLoading}
              icon={Wrench}
              className="w-full justify-start"
            >
              {toolsLoading ? 'Abrindo…' : 'Ferramentas'}
            </Button>
          )}
        </div>

        {/* Separador - apenas se houver itens abaixo */}
        {(canViewReports || canAccessCalendar) && (
          <>
            <div className="my-4 border-t border-navy-700"></div>

            {/* Seção Relatórios e Agenda */}
            <div className="space-y-1">
              {canViewReports && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    closeAllModals();
                    setShowReports(true);
                  }}
                  icon={BarChart2}
                  className="w-full justify-start"
                >
                  Relatórios
                </Button>
              )}

              {canAccessCalendar && (
                <Button
                  variant="ghost"
                  onClick={() => onOpenModal('Agenda', 
                    <div className="w-full h-96">
                      <Calendar className="w-full h-full text-gray-400" />
                      <p className="text-center mt-4">Funcionalidade em desenvolvimento</p>
                    </div>, 
                    'lg'
                  )}
                  icon={Calendar}
                  className="w-full justify-start"
                >
                  Agenda
                </Button>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  // ✅ CORREÇÃO: Validação de role movida para renderização condicional no JSX
  // NÃO usar early return após hooks - isso causa erro "Rendered fewer hooks than expected"
  if (!role) {
    return (
      <div className="h-full flex items-center justify-center bg-navy-900">
        <LoadingSpinner />
      </div>
    );
  }

  // Renderizar página de Gerenciar Arquivos se selecionada
  // Validação via permissionConfig (admin sempre tem acesso via hook)
  if (currentPage === 'ger-arquivos') {
    const permission = featurePermissions['general.can_manage_files'];
    if (permission?.allowed) {
      return (
        <GerArquivosPageRefatorada onBack={() => setCurrentPage('menu')} />
      );
    } else {
      // Fallback: se não tiver permissão, mostrar mensagem amigável
      return (
        <PermissionDenied 
          status={permission?.status || 'not_configured'}
          title="Gerenciador de Arquivos"
          onBack={() => setCurrentPage('menu')}
        />
      );
    }
  }

  // Renderizar Debugger se selecionado (substitui o MainMenu completamente)
  if (showDebugger && canAccessDebugger) {
    return (
      <DebuggerModal onClose={() => setShowDebugger(false)} />
    );
  }

  return (
    <div className="h-full flex flex-col bg-navy-900">
      {/* Header com Logo e Role Selector */}
      <div className="px-4 py-4 border-b border-navy-700">
        <div className="flex items-center justify-between gap-2">
          {/* Logo */}
          <div className="flex-shrink-0 flex-1 flex items-center justify-center">
            <img 
              src={getTenantLogo()} 
              alt="SysOnePrint" 
              className="h-11 w-auto object-contain"
            />
          </div>
          
          {/* Role Selector - aparece apenas se houver múltiplas permissões */}
          <div className="flex-shrink-0">
            <RoleSelector />
          </div>
        </div>
      </div>
      
      {/* Navegação Principal */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1">
          {renderClientRevendaButtons()}
          {renderAdminButtons()}
          {renderProducaoButtons()}
        </div>
      </nav>

      {/* Rodapé com Ação Principal */}
      {canSave && featurePermissions['general.can_create_new_order']?.allowed && (
        <div className="px-4 py-3 border-t border-navy-700">
          <Button
            variant="warning"
            onClick={onNewOrder}
            icon={FileText}
            className="w-full"
          >
            Novo Orçamento
          </Button>
        </div>
      )}

      {showReports && canViewReports && (
        <Reports onClose={() => setShowReports(false)} />
      )}
      {showOrderTracking && featurePermissions['orders.can_track']?.allowed && (
        <OrderTracking onClose={() => setShowOrderTracking(false)} />
      )}

      {showSettings && featurePermissions['general.can_access_settings']?.allowed && (
        <Settings onClose={() => setShowSettings(false)} />
      )}

      {!IS_PRODUCTION && showSaveTemp && (
        <SaveTempModal 
          onClose={() => setShowSaveTemp(false)} 
          onSaved={(draft) => {
            setTempDraft(draft);
          }}
        />
      )}

      {!IS_PRODUCTION && tempDraft && (
        <OrderDetailsTempWindow
          onClose={() => setTempDraft(null)}
          draft={tempDraft}
        />
      )}

    </div>
  );
}
